import { isStringList } from "@/lib/saved-filters";
import { OFFICIAL_PRESETS, type BookWeights } from "@/lib/weights-config";

export const PRICING_MODEL_FILTER_NAME = "pricing-model";

export type PricingModel = {
  readonly weights: BookWeights | null;
  readonly preset: string | null;
  readonly requiredBooks: readonly string[];
};

export function isWeights(value: unknown): value is BookWeights {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    && Object.values(value).every((weight) => typeof weight === "number" && Number.isFinite(weight) && weight >= 0);
}

export function isCustomPricingModel(source: Pick<PricingModel, "weights" | "preset">): boolean {
  return source.weights !== null || source.preset !== null;
}

export function pricingModelOf(source: PricingModel): PricingModel {
  return {
    weights: source.weights,
    preset: source.preset,
    requiredBooks: isCustomPricingModel(source) ? [...source.requiredBooks] : [],
  };
}

export function changedPricingModel(previous: PricingModel, next: PricingModel): PricingModel | null {
  const model = pricingModelOf(next);
  return pricingModelsEqual(pricingModelOf(previous), model) ? null : model;
}

export function withKnownPreset(model: PricingModel, userPresetNames: readonly string[]): PricingModel {
  if (model.preset === null || userPresetNames.includes(model.preset)) return model;
  const official = OFFICIAL_PRESETS.find((preset) => preset.key === model.preset || preset.label === model.preset);
  return official ? { ...model, weights: { ...official.weights } } : { ...model, preset: null };
}

export function coercePricingModel(value: unknown): PricingModel | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const outer = value as Record<string, unknown>;
  if (outer.v !== 1 || !outer.state || typeof outer.state !== "object" || Array.isArray(outer.state)) return null;
  const raw = outer.state as Record<string, unknown>;
  if (raw.weights !== null && !isWeights(raw.weights)) return null;
  if (raw.preset !== null && (typeof raw.preset !== "string" || raw.preset.length === 0)) return null;
  if (!isStringList(raw.requiredBooks)) return null;
  return pricingModelOf({ weights: raw.weights, preset: raw.preset, requiredBooks: raw.requiredBooks });
}

export function pricingModelsEqual(a: PricingModel, b: PricingModel): boolean {
  return a.preset === b.preset && weightsEqual(a.weights, b.weights) && sameBooks(a.requiredBooks, b.requiredBooks);
}

function weightsEqual(a: BookWeights | null, b: BookWeights | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
}

function sameBooks(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const books = new Set(a);
  return b.every((book) => books.has(book));
}
